(() => {
  if (customElements.get('collection-catalogue')) return;
  const fold = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  class CollectionCatalogue extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.grid = this.querySelector('[data-grid]');
      this.form = this.querySelector('[data-filters]');
      this.search = this.querySelector('input[data-search]');
      this.sort = this.querySelector('[data-sort]');
      this.searchClear = this.querySelector('[data-clear-search]');
      this.setupSort();
      this.setupDisclosures();
      this.low = this.form.elements.namedItem('min_price');
      this.high = this.form.elements.namedItem('max_price');
      this.lowRange = this.querySelector('[data-min-range]');
      this.highRange = this.querySelector('[data-max-range]');
      this.status = this.querySelector('[data-load-status]');
      this.setupNavigation();
      document.addEventListener('shopify:section:load', () => this.setupNavigation(), { signal });
      this.mobile = matchMedia('(max-width: 899px)');
      const panel = this.querySelector('[data-filter-panel]');
      const resize = () => this.setDisclosure(panel, !this.mobile.matches, false);
      resize();
      this.mobile.addEventListener('change', resize, { signal });
      this.form.addEventListener('submit', event => event.preventDefault(), { signal });
      this.form.addEventListener('change', () => this.apply(true), { signal });
      this.sort.addEventListener('change', () => this.apply(true), { signal });
      this.search.addEventListener('input', () => {
        this.updateSearchUI();
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.apply('replace'), 250);
      }, { signal });
      this.searchClear?.addEventListener('click', () => {
        clearTimeout(this.searchTimer);
        this.search.value = '';
        this.apply('replace');
        this.search.focus();
      }, { signal });
      this.search.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); clearTimeout(this.searchTimer); this.apply('replace'); }
      }, { signal });
      this.querySelectorAll('[data-clear]').forEach(button => button.addEventListener('click', () => this.clear(), { signal }));
      for (const [slider, field] of [[this.lowRange, this.low], [this.highRange, this.high]]) {
        slider.addEventListener('input', () => { field.value = slider.value; this.apply(false); }, { signal });
        slider.addEventListener('change', () => this.apply(true), { signal });
      }
      this.querySelector('[data-retry]').addEventListener('click', () => this.load(), { signal });
      addEventListener('popstate', () => { if (this.ready) { this.readURL(); this.apply(false); } }, { signal });
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.searchTimer);
      this.resultsAnimation?.cancel();
      this.querySelectorAll('details').forEach(details => {
        details.collectionAnimation?.cancel();
        details.collectionAnimation = null;
        delete details.dataset.targetOpen;
        details.style.removeProperty('overflow');
      });
    }

    updateSearchUI() {
      if (this.searchClear) this.searchClear.hidden = !this.search.value;
    }

    setupSort() {
      const control = this.querySelector('[data-sort-control]');
      if (!control) return;
      control.querySelector('[data-sort-menu]')?.remove();
      const menu = document.createElement('details');
      menu.className = 'collection-reference__sort-menu';
      menu.dataset.sortMenu = '';
      const trigger = document.createElement('summary');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', 'CollectionSortList-' + this.dataset.sectionId);
      const value = document.createElement('span');
      trigger.append(value);
      trigger.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>');
      const list = document.createElement('div');
      list.id = trigger.getAttribute('aria-controls');
      list.className = 'collection-reference__sort-list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-labelledby', this.sort.getAttribute('aria-labelledby'));
      for (const option of this.sort.options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.sortOption = option.value;
        button.setAttribute('role', 'option');
        button.textContent = option.textContent;
        button.tabIndex = -1;
        list.append(button);
      }
      menu.append(trigger, list);
      control.append(menu);
      this.sort.hidden = true;
      this.sortMenu = menu;
      this.sortTrigger = trigger;
      this.sortValue = value;
      this.sortList = list;
      const { signal } = this.controller;
      trigger.addEventListener('click', event => { if (this.sort.disabled) event.preventDefault(); }, { signal });
      list.addEventListener('click', event => {
        const option = event.target.closest('[data-sort-option]');
        if (!option || this.sort.disabled) return;
        this.sort.value = option.dataset.sortOption;
        this.sort.dispatchEvent(new Event('change', { bubbles: true }));
        menu.open = false;
        trigger.focus();
      }, { signal });
      menu.addEventListener('toggle', () => {
        trigger.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) {
          const rect = trigger.getBoundingClientRect(), height = list.offsetHeight;
          menu.classList.toggle('opens-up', rect.bottom + height + 12 > innerHeight && rect.top > height + 12);
        }
      }, { signal });
      document.addEventListener('pointerdown', event => { if (!menu.contains(event.target)) menu.open = false; }, { signal });
      menu.addEventListener('focusout', event => { if (!menu.contains(event.relatedTarget)) menu.open = false; }, { signal });
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); menu.open = false; trigger.focus(); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || this.sort.disabled) return;
        event.preventDefault();
        const options = [...list.children], index = options.indexOf(document.activeElement);
        let next = options.findIndex(option => option.dataset.sortOption === this.sort.value);
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = options.length - 1;
        else if (index >= 0) next = (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        menu.open = true;
        options[Math.max(0, next)].focus();
      }, { signal });
      this.updateSortUI();
    }

    updateSortUI() {
      if (!this.sortMenu) return;
      this.sortValue.textContent = this.sort.selectedOptions[0]?.textContent || '';
      this.sortTrigger.setAttribute('aria-disabled', String(this.sort.disabled));
      for (const option of this.sortList.children) {
        const selected = option.dataset.sortOption === this.sort.value;
        option.setAttribute('aria-selected', String(selected));
        option.tabIndex = selected ? 0 : -1;
      }
      if (this.sort.disabled) this.sortMenu.open = false;
    }

    setupDisclosures() {
      this.querySelectorAll('.collection-reference__filter-group, [data-filter-panel]').forEach(details => {
        const summary = details.querySelector(':scope > summary');
        summary.setAttribute('aria-expanded', String(details.open));
        summary.addEventListener('click', event => {
          event.preventDefault();
          const open = details.dataset.targetOpen ? details.dataset.targetOpen === 'true' : details.open;
          this.setDisclosure(details, !open);
        }, { signal: this.controller.signal });
      });
    }

    setDisclosure(details, open, animate = true) {
      const summary = details.querySelector(':scope > summary');
      const start = details.getBoundingClientRect().height;
      details.collectionAnimation?.cancel();
      details.dataset.targetOpen = String(open);
      summary.setAttribute('aria-expanded', String(open));
      if (!animate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        details.collectionAnimation = null;
        details.open = open;
        delete details.dataset.targetOpen;
        details.style.removeProperty('overflow');
        return;
      }
      details.open = true;
      const style = getComputedStyle(details);
      const closed = summary.getBoundingClientRect().height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const end = open ? details.getBoundingClientRect().height : closed;
      details.style.overflow = 'hidden';
      const animation = details.animate([{ height: start + 'px' }, { height: end + 'px' }], { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
      details.collectionAnimation = animation;
      animation.finished.then(() => {
        if (details.collectionAnimation !== animation) return;
        details.open = open;
        delete details.dataset.targetOpen;
        details.style.removeProperty('overflow');
        details.collectionAnimation = null;
      }).catch(() => {});
    }

    setupNavigation() {
      const navigation = [...document.querySelectorAll('[data-series-navigation]')].find(node => node.dataset.collectionHandle === this.dataset.collectionHandle);
      if (!navigation || this.navigation === navigation) return;
      this.navigation = navigation;
      navigation.style.setProperty('--cs-width', this.style.getPropertyValue('--cr-width'));
      this.prepareImageBackgrounds(navigation);
      const { signal } = this.controller;
      navigation.addEventListener('click', event => {
        const choice = event.target.closest('[data-series-choice]');
        if (!choice || !this.ready || this.dataset.collectionHandle !== 'impresoras-3d' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.form.querySelectorAll('[name="series"]').forEach(input => { input.checked = input.value === choice.dataset.seriesChoice; });
        this.apply(true);
      }, { signal });
      if (this.ready) this.apply(false);
    }

    prepareImageBackgrounds(container) {
      container.dataset.maskActive = 'true';
      this.backgroundMasks ??= new Map();
      const createMask = async image => {
        await image.decode();
        const ratio = 256 / Math.max(image.naturalWidth, image.naturalHeight);
        const width = Math.max(1, Math.round(image.naturalWidth * ratio)), height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height).data;
        const corners = [0, width - 1, (height - 1) * width, width * height - 1];
        const bounds = exterior => {
          let left=width, top=height, right=-1, bottom=-1;
          for (let y=0;y<height;y++) for (let x=0;x<width;x++) {
            const index=y*width+x;
            if (pixels[index*4+3]<32 || exterior?.[index]) continue;
            left=Math.min(left,x); right=Math.max(right,x); top=Math.min(top,y); bottom=Math.max(bottom,y);
          }
          return right>=left ? {left,top,right,bottom,width,height} : null;
        };
        if (!corners.every(index => pixels[index * 4 + 3] > 245 && [0, 1, 2].every(channel => pixels[index * 4 + channel] >= 240))) return {mask:'',bounds:bounds()};
        const exterior = new Uint8Array(width * height), queue = new Int32Array(width * height);
        let head = 0, tail = 0;
        const visit = index => {
          if (exterior[index] || ![0, 1, 2].every(channel => pixels[index * 4 + channel] >= 242)) return;
          exterior[index] = 1; queue[tail++] = index;
        };
        for (let x = 0; x < width; x++) { visit(x); visit((height - 1) * width + x); }
        for (let y = 0; y < height; y++) { visit(y * width); visit(y * width + width - 1); }
        while (head < tail) {
          const index = queue[head++], x = index % width;
          if (x) visit(index - 1);
          if (x < width - 1) visit(index + 1);
          if (index >= width) visit(index - width);
          if (index < width * (height - 1)) visit(index + width);
        }
        const mask = context.createImageData(width, height);
        for (let index = 0; index < exterior.length; index++) {
          const offset = index * 4;
          mask.data[offset] = mask.data[offset + 1] = mask.data[offset + 2] = 255;
          mask.data[offset + 3] = exterior[index] ? 0 : pixels[offset + 3];
        }
        context.putImageData(mask, 0, 0);
        return {mask:canvas.toDataURL(),bounds:bounds(exterior)};
      };
      container.querySelectorAll('img').forEach(image => {
        const fit = async () => {
          const url = image.currentSrc || image.src;
          if (image.dataset.maskSource === url) return;
          image.dataset.background = 'loading';
          image.style.removeProperty('mask-image'); image.style.removeProperty('-webkit-mask-image');
          try {
            if (!this.backgroundMasks.has(url)) this.backgroundMasks.set(url, createMask(image));
            const {mask,bounds} = await this.backgroundMasks.get(url);
            if (!image.isConnected || (image.currentSrc || image.src) !== url) return;
            if (mask) { image.style.maskImage = `url("${mask}")`; image.style.webkitMaskImage = `url("${mask}")`; }
            const frame=image.closest('[data-series-image]');
            if (frame && bounds && frame.clientWidth && frame.clientHeight) {
              const ratio=frame.clientWidth/frame.clientHeight;
              const contain=Math.min(ratio/bounds.width,1/bounds.height);
              const scale=Math.min(.9*ratio/((bounds.right-bounds.left+1)*contain),.9/((bounds.bottom-bounds.top+1)*contain),8);
              frame.style.setProperty('--cs-image-scale',scale.toFixed(4));
              frame.style.setProperty('--cs-image-x',(-(bounds.left+bounds.right+1-bounds.width)/2*contain*scale/ratio*100).toFixed(2)+'%');
              frame.style.setProperty('--cs-image-y',(-(bounds.top+bounds.bottom+1-bounds.height)/2*contain*scale*100).toFixed(2)+'%');
              image.dataset.subjectFit='true';
            }
            image.dataset.maskSource = url;
            image.dataset.background = mask ? 'masked' : 'native';
          } catch { this.backgroundMasks.delete(url); if ((image.currentSrc || image.src) === url) image.dataset.background = 'fallback'; }
        };
        image.addEventListener('load', fit, { signal: this.controller.signal });
        if (image.complete && image.naturalWidth) fit();
      });
    }


    prepareHomeImage(card) {
      const image = card.querySelector('.collection-reference-card__media img');
      if (!image) return;
      const normalize = async () => {
        const source = image.currentSrc || image.src;
        if (!source || image.dataset.normalizedSource === source) return;
        image.dataset.normalizedSource = source;
        await customElements.whenDefined('products-collections');
        if (!this.isConnected || image.dataset.normalizedSource !== source) return;
        await customElements.get('products-collections').prototype.normalizeProductImage.call(this, image, source);
      };
      if (!image.dataset.homeImageBound) {
        image.addEventListener('load', normalize, { signal: this.controller.signal });
        image.dataset.homeImageBound = 'true';
      }
      if (image.complete && image.naturalWidth) normalize();
    }

    async syncHomeCards() {
      if (this.dataset.syncHome !== 'true') return;
      try {
        const url = new URL(this.dataset.homeUrl, location.origin);
        url.searchParams.set('p3d_collection_cards', '1');
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-cache', signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(10000)]) });
        if (!response.ok) throw new Error('Homepage unavailable');
        const home = new DOMParser().parseFromString(await response.text(), 'text/html');
        const originals = [...home.querySelectorAll('[data-home-card-id][data-product-handle]')];
        const navigationCards = [...document.querySelectorAll('.p3d-floating-menu__product[data-product-handle]')];
        for (const item of this.items) {
          const navigation = navigationCards.find(node => node.dataset.productHandle === item.node.dataset.productHandle);
          const subtitle = item.node.querySelector('.collection-reference-card__subtitle');
          if (navigation && subtitle) {
            const text = navigation.querySelector('.p3d-floating-menu__product-subtitle')?.textContent.trim() || '';
            subtitle.textContent = subtitle.title = text;
            subtitle.hidden = !text;
          }
          const source = originals.find(node => node.dataset.productHandle === item.node.dataset.productHandle);
          if (!source) continue;
          const title = source.querySelector('.product-card--creality__title a')?.textContent.trim();
          if (title) {
            const link = item.node.querySelector('.collection-reference-card__title a');
            link.textContent = link.title = title;
            item.title = item.node.dataset.title = title;
            item.search = fold(item.node.dataset.search + ' ' + title);
          }
          const sourceImage = source.querySelector('.product-card--creality__image--primary');
          const image = item.node.querySelector('.collection-reference-card__media img');
          if (image && sourceImage) {
            const currentURL = new URL(image.getAttribute('src'), location.origin);
            const sourceURL = new URL(sourceImage.getAttribute('src'), location.origin);
            if (currentURL.pathname.split('/').at(-1) !== sourceURL.pathname.split('/').at(-1) || currentURL.searchParams.get('v') !== sourceURL.searchParams.get('v')) {
              image.src = sourceImage.getAttribute('src');
              image.srcset = sourceImage.getAttribute('srcset') || '';
            }
            image.alt = title || image.alt;
          }
          const badge = source.querySelector('[data-products-badge]');
          const series = source.closest('[data-products-series]');
          const badgeText = badge?.dataset.ownBadge || series?.dataset.defaultBadge || badge?.dataset.fallbackBadge || '';
          const showBadge = badge && badge.dataset.hideBadge !== 'true' && badgeText && (badge.dataset.ownBadge || series?.dataset.showBadge === 'true');
          const recommendation = item.node.querySelector('product-recommendation');
          if (recommendation) { recommendation.textContent = showBadge ? badgeText : ''; recommendation.hidden = !showBadge; }
          const css = source.closest('.shopify-section')?.querySelector('style')?.textContent || '';
          for (const role of ['title', 'price', 'badge']) {
            const weights = [...css.matchAll(new RegExp('--collection-card-' + role + '-weight:\\s*(\\d+)', 'g'))].map(match => match[1]);
            if (weights.length) {
              item.node.style.setProperty('--cr-home-' + role + '-weight', weights[0]);
              item.node.style.setProperty('--cr-home-' + role + '-weight-mobile', weights.at(-1));
              if (role === 'badge') item.node.style.setProperty('--cr-badge-weight', weights[0]);
            }
          }
          // Read the same CSS tokens used by Products, then apply series/card size overrides.
          const tokens = new Map([...css.matchAll(/--collection-(card|cart)-([a-z-]+):\s*([^;{}]+);/g)].map(match => [match[1] + '-' + match[2], match[3].trim()]));
          for (const match of css.matchAll(/--collection-card-([a-z-]+):\s*([^;{}]+);/g)) {
            const role = match[1];
            // Keep collection spacing editable without changing homepage type or image sizing.
            if (role.endsWith('-weight') || ['image-top','image-text-gap','text-side-gap','text-bottom-gap','badge-title-gap','title-subtitle-gap','subtitle-price-gap','price-button-gap'].includes(role)) continue;
            item.node.style.setProperty('--cr-home-' + role, match[2].trim());
          }
          for (const ancestor of [source.closest('[role="tabpanel"]'), source]) {
            if (!ancestor) continue;
            for (const role of ['title', 'subtitle', 'price', 'badge']) {
              const size = ancestor.style.getPropertyValue('--collection-card-' + role + '-size');
              if (size) item.node.style.setProperty('--cr-home-' + role + '-size', size);
            }
          }
          const badgeColor = css.match(/--sales-card-badge:\s*([^;]+);/)?.[1] || '#f04438';
          item.node.style.setProperty('--cr-home-badge-color', badgeColor);
          item.node.style.setProperty('--cr-home-subtitle-color', '#8b8b8b');
          item.node.style.setProperty('--cr-home-cart-height', tokens.get('cart-height') || '40px');
          item.node.style.setProperty('--cr-home-cart-icon-size', tokens.get('cart-icon-size') || '26px');
          item.node.dataset.homeSynced = 'true';
          this.prepareHomeImage(item.node);

        }
        this.dataset.homeSyncState = 'ready';
        this.apply(false);
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.homeSyncState = 'fallback';
      }
    }

    async fetchPage(pageURL) {
      const url = new URL(pageURL, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid collection source');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('collection-catalogue');
      if (!source?.querySelector('[data-grid]')) throw new Error('Collection response incomplete');
      return source;
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      this.ready = false;
      this.querySelector('fieldset').disabled = true;
      this.search.disabled = this.sort.disabled = true;
      this.status.textContent = this.dataset.loading;
      this.querySelector('[data-retry]').hidden = true;
      try {
        let next = this.dataset.nextPage;
        // Filtering always covers the full collection, including direct page-2 links.
        if (this.dataset.page !== '1' || this.dataset.filtered === 'true') {
          const first = await this.fetchPage(this.dataset.baseUrl);
          this.grid.replaceChildren(...first.querySelector('[data-grid]').children);
          next = first.dataset.nextPage;
          this.dataset.page = '1';
          this.dataset.filtered = 'false';
        }
        const visited = new Set();
        while (next) {
          if (visited.has(next)) throw new Error('Repeated collection page');
          visited.add(next);
          const page = await this.fetchPage(next);
          this.grid.append(...page.querySelector('[data-grid]').children);
          next = page.dataset.nextPage;
        }
        this.dataset.nextPage = '';
        // Supplemental laser cards share this catalogue without changing Shopify collection membership.
        const productIds = new Set();
        for (const card of this.grid.querySelectorAll('[data-catalogue-card]')) {
          if (productIds.has(card.dataset.productId)) card.remove();
          else productIds.add(card.dataset.productId);
        }
        this.items = [...this.grid.querySelectorAll('[data-catalogue-card]')].map((node, index) => ({
          node, index, title: node.dataset.title, search: fold(node.dataset.search),
          series: node.dataset.series, price: Number(node.dataset.price) / 100,
          configuration: node.dataset.configuration, status: node.dataset.status, volume: node.dataset.volume, enclosure: node.dataset.enclosure, printing: node.dataset.printing,
          available: node.dataset.available === 'true', published: Number(node.dataset.published) || 0,
        }));
        this.maximum = Math.max(1, Math.ceil(Math.max(0, ...this.items.map(item => item.price))));
        for (const field of [this.low, this.high, this.lowRange, this.highRange]) field.max = this.maximum;
        this.low.defaultValue = '0';
        this.high.defaultValue = String(this.maximum);
        this.querySelector('fieldset').disabled = false;
        this.search.disabled = this.sort.disabled = false;
        this.status.textContent = '';
        this.querySelector('[data-load-more]')?.setAttribute('hidden', '');
        this.ready = true;
        this.readURL();
        this.apply(false);
        this.dataset.ready = 'true';
        if (this.dataset.machine === 'true') {
          this.prepareImageBackgrounds(this.grid);
          if (this.dataset.syncHome === 'true') this.items.forEach(item => this.prepareHomeImage(item.node));
        }
        this.syncHomeCards();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = this.dataset.loadError;
          this.querySelector('[data-retry]').hidden = false;
          // Retry starts at page one, so partially appended pages cannot duplicate cards.
          this.dataset.page = '0';
        }
      } finally { this.loading = false; }
    }

    readURL() {
      const params = new URL(location.href).searchParams;
      const families = (params.get('serie') || '').split(',');
      this.form.querySelectorAll('[name="series"]').forEach(field => { field.checked = families.includes(field.value); });
      this.form.querySelectorAll('[name="availability"]').forEach(field => { field.checked = field.value === params.get('availability'); });
      for (const group of ['configuration', 'volume', 'enclosure', 'printing']) {
        const values = (params.get(group) || '').split(',');
        this.form.querySelectorAll('[name="' + group + '"]').forEach(field => { field.checked = values.includes(field.value); });
      }
      this.search.value = params.get('q') || '';
      this.low.value = params.get('min_price') || '0';
      this.high.value = params.get('max_price') || String(this.maximum);
      const sort = params.get('sort') || params.get('sort_by') || 'featured';
      this.sort.value = [...this.sort.options].some(option => option.value === sort) ? sort : 'featured';
    }

    clear() {
      if (!this.ready) return;
      clearTimeout(this.searchTimer);
      this.form.reset();
      this.search.value = '';
      this.sort.value = 'featured';
      this.low.value = '0';
      this.high.value = String(this.maximum);
      this.apply(true);
    }

    apply(historyMode) {
      if (!this.ready) return;
      this.updateSearchUI();
      this.updateSortUI();
      const terms = fold(this.search.value).split(/\s+/).filter(Boolean);
      const families = [...this.form.querySelectorAll('[name="series"]:checked')].map(field => field.value);
      const stock = this.form.querySelector('[name="availability"]:checked')?.value || '';
      const laserOnly = this.dataset.collectionHandle === 'lasers' || families.length === 1 && families[0] === 'laser';
      this.querySelectorAll('[data-printer-filter]').forEach(group => {
        group.hidden = laserOnly;
        if (laserOnly) group.querySelectorAll('input').forEach(input => { input.checked = false; });
      });
      const groups = Object.fromEntries(['configuration', 'volume', 'enclosure', 'printing'].map(group => [group, [...this.form.querySelectorAll('[name="' + group + '"]:checked')].map(input => input.value)]));
      const low = Math.max(0, Math.min(this.maximum, Number(this.low.value) || 0));
      const upper = this.high.value === '' ? this.maximum : Number(this.high.value);
      const high = Math.max(low, Math.min(this.maximum, Number.isFinite(upper) ? upper : this.maximum));
      this.low.value = this.lowRange.value = String(low);
      this.high.value = this.highRange.value = String(high);
      const money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: this.dataset.currency || 'MXN' });
      this.lowRange.setAttribute('aria-valuetext', money.format(low));
      this.highRange.setAttribute('aria-valuetext', money.format(high));
      const fill = this.querySelector('[data-range-fill]');
      fill.style.left = (low / this.maximum * 100) + '%';
      fill.style.width = ((high - low) / this.maximum * 100) + '%';
      const sort = this.sort.value;
      const ordered = [...this.items].sort((a, b) => {
        if (sort === 'price-ascending') return a.price - b.price || a.index - b.index;
        if (sort === 'price-descending') return b.price - a.price || a.index - b.index;
        if (sort === 'title-ascending') return a.title.localeCompare(b.title, 'es');
        if (sort === 'title-descending') return b.title.localeCompare(a.title, 'es');
        if (sort === 'created-descending') return b.published - a.published || a.index - b.index;
        return a.index - b.index;
      });
      let visible = 0;
      const visibleItems = [];
      for (const [index, item] of ordered.entries()) {
        const stockMatch = this.dataset.machine === 'true' ? item.status === stock : item.available === (stock === 'in');
        const match = terms.every(term => item.search.includes(term)) && (!families.length || families.includes(item.series)) && (!stock || stockMatch) && Object.entries(groups).every(([group, values]) => !values.length || values.includes(item[group])) && item.price >= low && item.price <= high;
        item.node.hidden = !match;
        if (match) { visible++; visibleItems.push(item.node.dataset.productId); }
        if (this.grid.children[index] !== item.node) this.grid.insertBefore(item.node, this.grid.children[index] || null);
      }
      const signature = visibleItems.join(',');
      if (this.resultSignature !== undefined && signature !== this.resultSignature && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.resultsAnimation?.cancel();
        this.resultsAnimation = this.grid.animate([{ opacity: .55, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220, easing: 'ease-out' });
      }
      this.resultSignature = signature;
      this.querySelector('[data-product-count]').textContent = visible + ' ' + (visible === 1 ? this.dataset.countOne : this.dataset.countMany);
      this.navigation?.querySelectorAll('[data-series-choice]').forEach(choice => {
        const navigationFamilies = families.length || !this.dataset.collectionHandle.startsWith('serie-') ? families : [this.dataset.collectionHandle.slice(-1)];
        const laser = laserOnly;
        const active = choice.dataset.seriesChoice === 'laser' ? laser : !laser && (choice.dataset.seriesChoice ? navigationFamilies.length === 1 && navigationFamilies[0] === choice.dataset.seriesChoice : navigationFamilies.length === 0);
        active ? choice.setAttribute('aria-current', 'page') : choice.removeAttribute('aria-current');
      });
      const count = (stock ? 1 : 0) + Object.values(groups).reduce((sum, values) => sum + values.length, 0) + (terms.length ? 1 : 0) + (low > 0 || high < this.maximum ? 1 : 0);
      this.querySelector('[data-filter-count]').textContent = String(count);
      this.querySelector('[data-mobile-filter-count]').textContent = count ? '(' + count + ')' : '';
      this.querySelector('[data-empty]').hidden = visible > 0;
      if (historyMode) {
        const url = new URL(location.href);
        for (const key of [...url.searchParams.keys()]) if (key === 'page' || key === 'sort_by' || key.startsWith('filter.')) url.searchParams.delete(key);
        const values = { q: this.search.value.trim(), serie: families.join(','), availability: stock, ...Object.fromEntries(Object.entries(groups).map(([key, selected]) => [key, selected.join(',')])), min_price: low > 0 ? low : '', max_price: high < this.maximum ? high : '', sort: sort === 'featured' ? '' : sort };
        for (const [key, value] of Object.entries(values)) value !== '' ? url.searchParams.set(key, String(value)) : url.searchParams.delete(key);
        if (url.href !== location.href) history[historyMode === 'replace' ? 'replaceState' : 'pushState']({}, '', url);
      }
    }
  }
  customElements.define('collection-catalogue', CollectionCatalogue);
})();

